/* eslint-disable @typescript-eslint/no-empty-function */
// @file Vuex Store module for Realtime functionality
import * as ActionCable from '@rails/actioncable'
import { getSocket } from 'bits/brahms'
import { captureException } from 'bits/error_tracker'
import window from 'bits/global'
import { __ } from 'bits/intl'
import { isTokenExpired } from 'bits/jwt'
import ping from 'bits/ping'
import debounce from 'lodash.debounce'
import * as Phoenix from 'phoenix'
import PadletApi from 'surface/padlet_api'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

interface WallChannel extends ActionCable.Subscription {
  received: (data: {}) => void
  disconnect: () => void
  present: () => void
  absent: () => void
}

interface DeviceChannel extends ActionCable.Subscription {
  received: (data: {}) => void
}

interface RealtimeState {
  wallChannel: WallChannel | null
  wallChannelLastConnected: number | null
  wallChannelLastDisconnected: number | null
  deviceChannel: DeviceChannel | null
  useBrahms: boolean
  pingContributingStatusToken: string | null
  brahmsToken: string | null
  deviceId: string | null
  brahmsSocket: Phoenix.Socket | null
  wallBrahmsChannel: Phoenix.Channel | null
  deviceBrahmsChannel: Phoenix.Channel | null
  dispatchQueue: any[]
  isClearingQueue: boolean
  notificationUid: string | null
}

const defaultState = (): RealtimeState => ({
  wallChannel: null,
  wallChannelLastConnected: null,
  wallChannelLastDisconnected: null,
  deviceChannel: null,
  useBrahms: false,
  pingContributingStatusToken: null,
  brahmsToken: null,
  deviceId: null,
  brahmsSocket: null,
  wallBrahmsChannel: null,
  deviceBrahmsChannel: null,
  dispatchQueue: [],
  isClearingQueue: false,
  notificationUid: null,
})

function userNeedsFocus(rootGetters): boolean {
  const isNonChatPostBeingEdited = !!rootGetters['post/postBeingEditedCid']
  // for modal posting we won't suppress updates while the user is writing a post anymore
  return (
    (!rootGetters.isUsingNewComposerModal && isNonChatPostBeingEdited) ||
    !!rootGetters['reaction/postIdBeingReactedTo'] ||
    rootGetters['comment/isCommenting']
  )
}

const RECONNECT_DELAY = [50, 100, 200, 500, 1000, 2000, 5000]

const RealtimeModule: Module<RealtimeState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    areUpdatesPending(state): boolean {
      return state.dispatchQueue.length > 0
    },
    isConnected(state): boolean {
      const connectedTime = state.wallChannelLastConnected
      const disconnectedTime = state.wallChannelLastDisconnected
      if (!connectedTime) return false
      if (!disconnectedTime) return true
      return connectedTime > disconnectedTime
    },
    isRealtimeDownSinceLastSync(state, getters): (lastSyncTime: number) => boolean {
      return (lastSyncTime: number): boolean => {
        if (!getters.isConnected) return true
        const connectedTime = state.wallChannelLastConnected as number
        return lastSyncTime <= connectedTime
      }
    },
  },
  mutations: {
    INITIALIZE_STATE(state, data): void {
      Object.assign(state, data)
    },
    SET_WALL_CHANNEL(state, wallChannel): void {
      state.wallChannel = wallChannel
    },
    SET_DEVICE_CHANNEL(state, deviceChannel): void {
      state.deviceChannel = deviceChannel
    },
    SET_BRAHMS_SOCKET(state, brahmsSocket): void {
      state.brahmsSocket = brahmsSocket
    },
    SET_WALL_BRAHMS_CHANNEL(state, wallBrahmsChannel): void {
      state.wallBrahmsChannel = wallBrahmsChannel
    },
    SET_DEVICE_BRAHMS_CHANNEL(state, deviceBrahmsChannel): void {
      state.deviceBrahmsChannel = deviceBrahmsChannel
    },
    QUEUE_UPDATE_ACTION(state, action): void {
      state.dispatchQueue.push(action)
    },
    START_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = true
    },
    CLEAR_DISPATCH_QUEUE(state): void {
      state.dispatchQueue = []
    },
    END_CLEARING_DISPATCH_QUEUE(state): void {
      state.isClearingQueue = false
    },
    SET_NOTIFICATION_UID(state, uid): void {
      state.notificationUid = uid
    },
    CLEAR_NOTIFICATION_UID(state): void {
      state.notificationUid = null
    },
    WALL_CHANNEL_CONNECTED(state): void {
      state.wallChannelLastConnected = Date.now()
    },
    WALL_CHANNEL_DISCONNECTED(state): void {
      state.wallChannelLastDisconnected = Date.now()
    },
  },
  actions: {
    initializeState({ commit }, data): void {
      commit('INITIALIZE_STATE', data)
    },
    connect({ dispatch, state }, { actionCableConnection }: { actionCableConnection: ActionCable.Consumer }): void {
      dispatch('subscribeWallChannel', actionCableConnection)
      dispatch('subscribeDeviceChannel', actionCableConnection)
      dispatch('subscribeToStoreChanges')

      if (state.useBrahms) {
        dispatch('connectToBrahms', state.brahmsToken)
      }
    },

    connectToBrahms({ dispatch, commit }, brahmsToken): void {
      const brahmsConnection = getSocket(brahmsToken, (tries) => {
        if (tries - 1 >= RECONNECT_DELAY.length) {
          // stop reconnecting
          dispatch('disconnectBrahms')
        }
        return RECONNECT_DELAY[tries - 1] || 5000
      })

      if (brahmsConnection) {
        commit('SET_BRAHMS_SOCKET', brahmsConnection)
        brahmsConnection.onError(() => {
          if (isTokenExpired(brahmsToken)) {
            dispatch('disconnectBrahms')
            dispatch('refreshTokenAndConnect')
          }
        })
        brahmsConnection.connect()

        dispatch('subscribeWallBrahmsChannel')
        dispatch('subscribeDeviceBrahmsChannel')
      }
    },

    refreshTokenAndConnect({ dispatch, state }): void {
      PadletApi.BrahmsToken.get({ token: state.pingContributingStatusToken }).then((data) => {
        dispatch('connectToBrahms', data.brahmsToken)
      })
    },

    disconnectBrahms({ state }): void {
      state.brahmsSocket && state.brahmsSocket.disconnect()
    },

    disconnect({ state }): void {
      state.wallChannel && state.wallChannel.unsubscribe()
      state.deviceChannel && state.deviceChannel.unsubscribe()
    },

    absent({ state }): void {
      state.wallChannel && state.wallChannel.absent()
    },

    present({ state }): void {
      state.wallChannel && state.wallChannel.present()
    },

    subscribeWallChannel({ dispatch, commit, rootGetters }, actionCableConnection: ActionCable.Consumer): void {
      const wallId = rootGetters.wallId
      const wallChannel = actionCableConnection.subscriptions.create(
        { channel: 'WallChannel', wall_id: wallId },
        {
          received(data): void {
            dispatch('handleMessage', data)
          },
          disconnect(): void {
            this.absent()
          },
          connected(): void {
            commit('WALL_CHANNEL_CONNECTED')
          },
          disconnected(): void {
            commit('WALL_CHANNEL_DISCONNECTED')
          },
          present(): void {
            this.perform('present', { wall_id: wallId })
          },
          absent(): void {
            this.perform('absent', { wall_id: wallId })
          },
        },
      )
      commit('SET_WALL_CHANNEL', wallChannel)
    },

    subscribeWallBrahmsChannel({ commit, rootGetters, state }): void {
      const wallId = rootGetters.wallId
      if (state.brahmsSocket) {
        try {
          const wallBrahmsChannel: Phoenix.Channel = state.brahmsSocket.channel(`wall:${wallId}`)

          wallBrahmsChannel.on('new_msg', (msg) => {})
          wallBrahmsChannel.onClose((msg) => {})
          wallBrahmsChannel.onError((msg) => {})
          wallBrahmsChannel.join()

          commit('SET_WALL_BRAHMS_CHANNEL', wallBrahmsChannel)
        } catch (e) {
          captureException(e)
        }
      }
    },

    subscribeDeviceChannel({ dispatch, commit, state }, actionCableConnection: ActionCable.Consumer): void {
      const deviceChannel = actionCableConnection.subscriptions.create(
        { channel: 'DeviceChannel', device_id: state.deviceId },
        {
          received(data): void {
            dispatch('handleMessage', data)
          },
        },
      )
      commit('SET_DEVICE_CHANNEL', deviceChannel)
    },

    subscribeDeviceBrahmsChannel({ commit, state }): void {
      if (state.brahmsSocket) {
        try {
          const deviceBrahmsChannel: Phoenix.Channel = state.brahmsSocket.channel(`device:${state.deviceId}`)

          deviceBrahmsChannel.on('new_msg', (msg) => {})
          deviceBrahmsChannel.onClose((msg) => {})
          deviceBrahmsChannel.onError((msg) => {})
          deviceBrahmsChannel.join()

          commit('SET_DEVICE_BRAHMS_CHANNEL', deviceBrahmsChannel)
        } catch (e) {
          captureException(e)
        }
      }
    },

    subscribeToStoreChanges({ dispatch, getters, rootGetters }): void {
      const flushUpdates = (): void => {
        if (getters.areUpdatesPending && !userNeedsFocus(rootGetters)) {
          dispatch('dispatchAllQueuedActions')
        }
      }
      const debouncedFlushUpdates = debounce(flushUpdates, 200, { leading: true })
      this.subscribe((): void => debouncedFlushUpdates())
    },

    handleMessage({ dispatch, rootState }, messageObj): void {
      if (!messageObj || !messageObj.event || messageObj.uid === rootState.uid) return
      const payload = JSON.parse(messageObj.message)
      // Signal that realtime is working
      ping('realtime', { rate: 20000 })
      const dispatchNow = (type: string): Promise<any> => dispatch(type, payload, { root: true })
      const queue = (type: string): Promise<any> => dispatch('queueUpdateAction', { type, payload })
      // We add a delay if the post has an attachment, so that we have sufficient
      // lead time for request coalescing to come into play.
      // See original commit: https://github.com/padlet/mozart/commit/9f9e0d60f3934c0e1c6ef411af97e1e24f429151
      const delay = (type: string): void => {
        const upTo1andHalfSecDelay = (Math.random() + 0.5) * 1000
        payload.attachment
          ? setTimeout((): void => {
              queue(type)
            }, upTo1andHalfSecDelay)
          : queue(type)
      }

      // Right now we are calling 'remote' actions in the modules themselves.
      // Eventually, the modules will have no concept of remote updates.
      // They'll only have simple CRUD operations that'll be called by this guy.
      const handlers = {
        edit_wall(): void {
          dispatchNow('updateWall')
        },
        delete_wall(): void {
          window.location.href = '/'
        },
        add_section(): void {
          dispatchNow('section/newSectionRemote')
        },
        edit_section(): void {
          dispatchNow('section/editSectionRemote')
        },
        delete_section(): void {
          dispatchNow('section/deleteSectionRemote')
        },
        add_wish(): void {
          delay('post/newPostRemote')
        },
        edit_wish(): void {
          delay('post/editPostRemote')
        },
        edit_wish_order(): void {
          delay('post/editPostOrderRemote')
        },
        delete_wish(): void {
          dispatchNow('deletePostRemote')
        },
        add_comment(): void {
          queue('comment/newCommentRemote')
        },
        edit_comment(): void {
          queue('comment/editCommentRemote')
        },
        delete_comment(): void {
          queue('comment/deleteCommentRemote')
        },
        add_reaction(): void {
          dispatchNow('reaction/newReactionRemote')
        },
        edit_reaction(): void {
          dispatchNow('reaction/editReactionRemote')
        },
        delete_reaction(): void {
          dispatchNow('reaction/deleteReactionRemote')
        },
        add_connection(): void {
          dispatchNow('postConnection/newConnectionRemote')
        },
        delete_connection(): void {
          dispatchNow('postConnection/deleteConnectionRemote')
        },
        edit_table_layout(): void {
          queue('table/editLayoutRemote')
        },
        edit_table_row(): void {
          queue('table/editRowRemote')
        },
        edit_table_col(): void {
          queue('table/editColRemote')
        },
        contributing_status(): void {
          queue('contributingStatus/updateRemote')
        },
        ping_contributing_status(): void {
          queue('contributingStatus/pushSetIfActive')
        },
        login(): void {
          window.location.reload()
        },
        logout(): void {
          window.location.reload()
        },
      }

      const handler = handlers[messageObj.event]
      handler && handler()
    },

    queueUpdateAction({ dispatch, commit, getters, rootGetters }, { type, payload }): void {
      if (userNeedsFocus(rootGetters)) {
        if (!getters.areUpdatesPending) dispatch('setUpdatesPendingNotification')
        commit('QUEUE_UPDATE_ACTION', { type, payload })
      } else {
        dispatch(type, payload, { root: true })
      }
    },

    setUpdatesPendingNotification({ dispatch, commit }): void {
      dispatch(
        'snackbars/setSnackbar',
        {
          message: __('Padlet updated elsewhere.'),
          actionText: __('Update'),
          actionName: 'realtime/dispatchAllQueuedActions',
          persist: true,
        },
        { root: true },
      ).then((uid): void => commit('SET_NOTIFICATION_UID', uid))
    },

    clearUpdatesPendingNotification({ dispatch, commit, state }): void {
      dispatch('snackbars/clearSnackbar', state.notificationUid, { root: true })
      commit('CLEAR_NOTIFICATION_UID')
    },

    dispatchAllQueuedActions({ commit, dispatch, state }): void {
      if (state.isClearingQueue) return
      commit('START_CLEARING_DISPATCH_QUEUE')
      state.dispatchQueue.forEach(({ type, payload }): Promise<any> => dispatch(type, payload, { root: true }))
      dispatch('clearDispatchQueue')
      commit('END_CLEARING_DISPATCH_QUEUE')
    },

    clearDispatchQueue({ dispatch, commit }): void {
      dispatch('clearUpdatesPendingNotification')
      commit('CLEAR_DISPATCH_QUEUE')
    },
  },
}

export default RealtimeModule
export { RealtimeState }
